import { useCallback, useEffect, useState } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import { fetchEmployeeLeaves, buildLeaveDayMap } from '../../../utils/employeeLeaves';

/**
 * Operasyon Yönetimi verileri: çalışan ve proje listeleri, seçilen çalışanın
 * vizit/iş/izin kayıtları (takvim) ve gerektiğinde tüm çalışanların kayıtları
 * (Personel Raporları ve Detaylı Rapor).
 */
const useOperationsData = ({ currentDate, selectedEmployeeId, showPersonnelReports, errorText }) => {
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [visits, setVisits] = useState([]);
  const [calendarJobs, setCalendarJobs] = useState([]);
  // Seçili çalışanın onaylanmış izinleri: 'yyyy-mm-dd' → izin kayıtları
  const [leaveDayMap, setLeaveDayMap] = useState(() => new Map());
  const [isLoading, setIsLoading] = useState(false);

  // "All employees" dataset — lazy loaded only when Personnel Reports is
  // expanded or when generating a detailed report. Kept separate from the
  // per-employee `visits`/`calendarJobs` so the calendar stays lean.
  const [allVisits, setAllVisits] = useState([]);
  const [allCalendarJobs, setAllCalendarJobs] = useState([]);
  const [isLoadingAllData, setIsLoadingAllData] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);

  /**
   * 1. Fetch employees and projects from Firestore
   */
  useEffect(() => {
    const fetchEmployeesAndProjects = async () => {
      try {
        // Fetch employees
        const employeesSnapshot = await getDocs(collection(db, 'Employees'));
        const employeesData = employeesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setEmployees(employeesData);

        // Fetch projects
        const projectsSnapshot = await getDocs(collection(db, 'Projects'));
        const projectsData = projectsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching employees and projects:', error);
        toast.error(errorText.load);
      }
    };

    fetchEmployeesAndProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 2. Fetch visits and jobs for specific month or all visits
   */
  const fetchDataForMonth = async (date, employeeId) => {
    // Lazy loading: do not fetch visits/jobs until an employee filter is picked.
    // Avoids pulling the entire Visits + CalendarJobs collections on initial load.
    if (!employeeId) {
      setVisits([]);
      setCalendarJobs([]);
      setLeaveDayMap(new Map());
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch visits for the selected employee (matches SMO Lead Reports behavior)
      const visitsQuery = query(
        collection(db, 'Visits'),
        where('employeeId', '==', employeeId)
      );
      const visitsSnapshot = await getDocs(visitsQuery);
      const visitsData = visitsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch calendar jobs for the selected employee
      const jobsQueryRef = query(
        collection(db, 'CalendarJobs'),
        where('employeeId', '==', employeeId)
      );
      const jobsSnapshot = await getDocs(jobsQueryRef);
      const jobsData = jobsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      setVisits(visitsData);
      setCalendarJobs(jobsData);

      // Onaylanmış izinler (İzin Yönetimi kayıtları + onaylanan talepler).
      // Takvimde vizit/iş olmasa bile çalışanın izinli günleri görünür.
      try {
        const leaves = await fetchEmployeeLeaves(employeeId);
        setLeaveDayMap(buildLeaveDayMap(leaves));
      } catch (leaveError) {
        console.error('Error fetching leaves:', leaveError);
        setLeaveDayMap(new Map());
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(errorText.load);
      setIsLoading(false);
    }
  };

  /**
   * Fetch data when the employee filter changes. The query covers all of the
   * employee's visits/jobs (matches SMO Lead Reports), so moving between
   * months, weeks or days needs no new read; "Yenile" reloads on demand.
   */
  useEffect(() => {
    fetchDataForMonth(currentDate, selectedEmployeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeId]);

  const refresh = () => fetchDataForMonth(currentDate, selectedEmployeeId);

  /**
   * Fetch the full Visits + CalendarJobs collections across all employees.
   * Used by Personnel Reports and the detailed Excel report.
   */
  const fetchAllVisitsAndJobs = useCallback(async ({ force = false } = {}) => {
    try {
      setIsLoadingAllData(true);
      const [visitsSnapshot, jobsSnapshot] = await Promise.all([
        getDocs(collection(db, 'Visits')),
        getDocs(collection(db, 'CalendarJobs'))
      ]);
      const freshVisits = visitsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const freshJobs = jobsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllVisits(freshVisits);
      setAllCalendarJobs(freshJobs);
      setAllDataLoaded(true);
      return { visits: freshVisits, jobs: freshJobs };
    } catch (error) {
      console.error('Error fetching all visits/jobs:', error);
      toast.error(errorText.loadAll);
      return { visits: [], jobs: [] };
    } finally {
      setIsLoadingAllData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load the full dataset when Personnel Reports is expanded.
  useEffect(() => {
    if (showPersonnelReports && !allDataLoaded && !isLoadingAllData) {
      fetchAllVisitsAndJobs();
    }
  }, [showPersonnelReports, allDataLoaded, isLoadingAllData, fetchAllVisitsAndJobs]);

  return {
    employees,
    projects,
    visits,
    setVisits,
    calendarJobs,
    setCalendarJobs,
    leaveDayMap,
    isLoading,
    allVisits,
    allCalendarJobs,
    isLoadingAllData,
    allDataLoaded,
    setAllDataLoaded,
    fetchDataForMonth,
    refresh,
    fetchAllVisitsAndJobs,
  };
};

export default useOperationsData;
